import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Bot, Send, MessageCircle, User, Sparkles } from 'lucide-react';

const sampleQA = [
  {
    q: 'What is our overall ROI performance across MENA regions?',
    a: 'Overall ROI across MENA is trending at 1.8x. UAE and KSA lead with strongest efficiency, while Egypt shows variance tied to increased CPMs. Consider reallocating 8–12% from print to paid social for a projected +0.2x ROI uplift.'
  },
  {
    q: 'Which channels are underperforming and why?',
    a: 'Outdoor and Radio underperform in Q3 due to seasonal footfall and limited flight optimization. Creative fatigue detected on 3 top ad sets. Rotating creatives and shifting 10% to high-intent search should improve CPA by 9–13%.'
  },
  {
    q: 'Show me budget variance analysis by country',
    a: 'Variance: UAE -2% (under), KSA +5% (over), Egypt -7% (under). Recommendation: Move AED 120k from KSA to UAE performance campaigns to align with revenue pacing.'
  },
  {
    q: 'What are the top performing cities for outdoor advertising?',
    a: 'Top cities: Dubai (Sheikh Zayed Rd), Riyadh (King Fahd Rd), and Jeddah (Tahlia St). Best weekparts: Thu–Sat evening slots. CPMs stable; recommend maintaining current flighting with +15% weight in Riyadh.'
  },
  {
    q: 'Identify opportunities for budget reallocation',
    a: 'Reallocate 12% from low-CTR display to branded search and high-performing video placements. Expected: +6% conversion lift, -9% blended CPA.'
  },
  {
    q: 'Compare digital vs traditional media effectiveness',
    a: 'Digital drives 68% of attributed conversions at 0.7x the CPA of traditional. Traditional provides incremental lift for awareness, especially in KSA. Balanced split: 75% digital / 25% traditional for Q4.'
  }
];

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      type: 'bot',
      content: "Hello! I'm your AI Media Analytics Assistant for the MENA region. Ask me about ROI, budget variance, channel performance, or optimization ideas.",
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleAsk = (q) => {
    const user = { id: `u_${Date.now()}`, type: 'user', content: q, timestamp: new Date().toLocaleTimeString() };
    setMessages((prev) => [...prev, user]);
    setIsTyping(true);
    const found = sampleQA.find((x) => x.q === q);
    const answer = found ? found.a : 'Here is a simulated response. This page is a demo and does not call a backend.';
    setTimeout(() => {
      const bot = { id: `b_${Date.now()}`,'type':'bot', content: answer, timestamp: new Date().toLocaleTimeString() };
      setMessages((prev) => [...prev, bot]);
      setIsTyping(false);
    }, 600);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    handleAsk(input.trim());
    setInput('');
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AI Media Insights Chatbot</h1>
              <p className="text-xs text-gray-500">MENA Analytics Assistant • Simulated responses</p>
            </div>
          </div>
          <Badge className="bg-green-100 text-green-800">Beta</Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="shadow-2xl border-2 border-blue-100">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-xl">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  <CardTitle className="text-lg">Conversation</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[520px] overflow-y-auto p-4 space-y-4 bg-white rounded-b-xl">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-lg ${m.type === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`}>
                        <div className="text-sm whitespace-pre-line">{m.content}</div>
                        <div className="text-[10px] opacity-70 mt-1 flex items-center gap-2">
                          {m.type === 'user' ? <User className="h-3 w-3" /> : <Bot className="h-3 w-3" />}<span>{m.timestamp}</span>
                          {m.type === 'bot' && (
                            <Badge className="bg-green-100 text-green-800">AI</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-lg rounded-bl-sm p-3">
                        <div className="flex gap-1 items-center text-gray-500 text-xs">
                          <Sparkles className="h-3 w-3" />
                          Typing...
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4 border-t bg-white rounded-b-xl">
                  <div className="flex gap-2">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Ask about KPIs, ROI, regions..."
                      className="flex-1 text-sm"
                    />
                    <Button onClick={handleSend} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Insights</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-2">
                {sampleQA.slice(0, 4).map((item, idx) => (
                  <Button key={idx} variant="outline" className="justify-start text-left text-sm h-10" onClick={() => handleAsk(item.q)}>
                    <span className="truncate">{item.q}</span>
                  </Button>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sample Q&A</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {sampleQA.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-sm font-semibold text-gray-800">Q: {item.q}</div>
                    <div className="text-sm text-gray-700 mt-1">A: {item.a}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;


